import { useLingui } from '@lingui/react/macro';
import { Progress } from '@utima/ui';
import { ChevronDown, ChevronUp, CircleCheck } from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';

export type StatsSummaryShellProps = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  progressValue: number;
  progressMax: number;
  completePercent: number;
  isFullyComplete: boolean;
  /** Middle pill cluster rendered next to the progress bar. */
  summary: ReactNode;
  /** Expanded detail body, rendered below a `border-t` divider when expanded. */
  children: ReactNode;
};

/**
 * Presentational shell for a collapsed progress row + expand toggle +
 * expanded detail area. Extracted from `KBStatsSummary` so KB and Products
 * status bars share the exact same frame markup — see
 * `docs/implementation/2026-07-18-product-table-ux-and-feed-settings-modal/plan.md` (D1).
 *
 * KB's rendered DOM must stay unchanged; do not alter class names here
 * without re-verifying `/knowledge-base` pixel parity.
 */
export function StatsSummaryShell({
  icon: Icon,
  label,
  progressValue,
  progressMax,
  completePercent,
  isFullyComplete,
  summary,
  children,
}: StatsSummaryShellProps) {
  const { t } = useLingui();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className='mb-4 w-full overflow-hidden rounded-radius border border-border bg-white'>
      <div className='flex flex-wrap items-center gap-x-6 gap-y-3 p-3'>
        <div className='flex shrink-0 items-center gap-2.5'>
          <Icon className='size-4 shrink-0 text-muted-fg' />
          <span className='text-sm font-bold'>{label}</span>
        </div>

        <div className='flex min-w-40 flex-1 basis-48 items-center gap-3'>
          <Progress
            value={progressValue}
            max={progressMax}
            variant='success'
            size='xs'
            className='min-w-0 flex-1'
          />
          <span className='shrink-0 text-xs tabular-nums text-muted-fg'>
            {completePercent}%
          </span>
          {isFullyComplete && (
            <CircleCheck className='size-4 shrink-0 text-success' />
          )}
        </div>

        <div className='flex flex-wrap items-center gap-3'>{summary}</div>

        <button
          type='button'
          className='ml-auto shrink-0 rounded p-1.5 hover:bg-muted/50 lg:ml-2'
          aria-expanded={isExpanded}
          aria-label={isExpanded ? t`Hide details` : t`Show details`}
          onClick={() => setIsExpanded(open => !open)}
        >
          {isExpanded ? (
            <ChevronUp className='size-4' />
          ) : (
            <ChevronDown className='size-4' />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className='border-t border-border px-3 py-1.5'>{children}</div>
      )}
    </div>
  );
}
